import {
  Badge,
  Button,
  ChevronDownIcon,
  DropdownMenu,
  UpgradeBadge,
} from '@buffer-mono/popcorn'

import {
  useCurrentOrganization,
  useHasEntitlement,
} from '~publish/contexts/AccountContext'
import { formatCompactNumber } from '~publish/helpers/numberFormatters'

import { isPostTab } from './PostTabs'
import type { PostTab } from './PostTabs'
import type { TabConfig } from './types'

import styles from './PostTabsDropdown.module.css'

import type { JSX } from 'react'

const labelByTab: Record<PostTab, string> = {
  queue: 'Queue',
  drafts: 'Drafts',
  approvals: 'Approvals',
  sent: 'Sent',
}

type PostTabsDropdownProps = {
  tabs: TabConfig[]
  value: PostTab
  onChange: (tab: PostTab) => void
  countLimit?: number | null
  countLoading?: boolean
}

export function PostTabsDropdown({
  tabs,
  value,
  onChange,
  countLimit,
  countLoading = false,
}: PostTabsDropdownProps): JSX.Element {
  const hasApprovalEntitlement = useHasEntitlement('approvalFlow')
  const currentOrganization = useCurrentOrganization()

  // Guard against empty tabs array
  if (tabs.length === 0) {
    return <></>
  }

  const currentTab = tabs.find((tab) => tab.id === value) ?? tabs[0]
  const currentLabel = labelByTab[currentTab.id]
  const currentCount = currentTab.count

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger>
        <Button
          variant="tertiary"
          className={styles.trigger}
          aria-label="Select post tab"
        >
          {currentLabel}
          {!countLoading && (
            <Badge size="small" className={styles.badge}>
              {formatCompactNumber(currentCount ?? 0, countLimit)}
            </Badge>
          )}
          <ChevronDownIcon />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <DropdownMenu.RadioGroup
          value={value}
          onChange={(newValue): void => {
            if (isPostTab(newValue)) {
              onChange(newValue)
            }
          }}
        >
          {tabs.map((tab) => {
            const isApprovalsWithoutEntitlement =
              tab.id === 'approvals' &&
              !hasApprovalEntitlement &&
              currentOrganization?.id

            return (
              <DropdownMenu.RadioItem
                key={tab.id}
                value={tab.id}
                className={styles.item}
              >
                <span className={styles.label}>
                  {labelByTab[tab.id]}
                  {isApprovalsWithoutEntitlement ? (
                    <UpgradeBadge size="small" />
                  ) : (
                    !countLoading && (
                      <Badge size="small" className={styles.badge}>
                        {formatCompactNumber(tab.count ?? 0, countLimit)}
                      </Badge>
                    )
                  )}
                </span>
              </DropdownMenu.RadioItem>
            )
          })}
        </DropdownMenu.RadioGroup>
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
